import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import Text from '@/components/priority/Text/Text'
import Button from '@/components/priority/Button/Button'
import colors from '@/utils/styling/colors'

type PdfProofGalleryProps = {
  screenshotUrls: string[]
}

const MIN_ZOOM = 1
const MAX_ZOOM = 5
const ZOOM_STEP = 0.25
const WHEEL_ZOOM_FACTOR = 0.0015

const GalleryWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  padding: 24px;
  gap: 16px;
  background: ${colors('FloatingMenu')};
  overflow: hidden;
`

const ImageWrapper = styled.div<{ $isPanning: boolean; $canPan: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  max-height: calc(100vh - 260px);
  overflow: hidden;
  cursor: ${({ $isPanning, $canPan }) =>
    $isPanning ? 'grabbing' : $canPan ? 'grab' : 'default'};
  user-select: none;
  touch-action: none;
`

const Image = styled.img<{ $zoom: number; $panX: number; $panY: number }>`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
  transform: translate(${({ $panX }) => $panX}px, ${({ $panY }) => $panY}px)
    scale(${({ $zoom }) => $zoom});
  transform-origin: center center;
  transition: transform 0.05s ease-out;
  will-change: transform;
  pointer-events: none;
`

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`

const ZoomGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

export const PdfProofGallery = ({ screenshotUrls }: PdfProofGalleryProps) => {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 })
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const totalPages = screenshotUrls.length
  const activeUrl = screenshotUrls[activeIndex]
  const displayedPageNumber = activeIndex + 1
  const canPan = zoom > 1

  const resetView = useCallback(() => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [])

  useEffect(() => {
    resetView()
  }, [activeIndex, resetView])

  const goPrev = () => setActiveIndex((i) => Math.max(0, i - 1))
  const goNext = () => setActiveIndex((i) => Math.min(totalPages - 1, i + 1))

  const applyZoom = useCallback(
    (nextZoomRaw: number) => {
      const nextZoom = clamp(nextZoomRaw, MIN_ZOOM, MAX_ZOOM)
      setZoom(nextZoom)
      if (nextZoom === 1) {
        setPan({ x: 0, y: 0 })
      }
    },
    [setZoom, setPan],
  )

  const zoomIn = () => applyZoom(zoom + ZOOM_STEP)
  const zoomOut = () => applyZoom(zoom - ZOOM_STEP)

  useEffect(() => {
    const node = wrapperRef.current
    if (!node) return

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault()
      const delta = -event.deltaY * WHEEL_ZOOM_FACTOR
      applyZoom(zoom + delta * zoom)
    }

    node.addEventListener('wheel', handleWheel, { passive: false })
    return () => node.removeEventListener('wheel', handleWheel)
  }, [zoom, applyZoom])

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!canPan) return
    event.currentTarget.setPointerCapture(event.pointerId)
    setIsPanning(true)
    panStart.current = {
      x: event.clientX,
      y: event.clientY,
      panX: pan.x,
      panY: pan.y,
    }
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanning) return
    const dx = event.clientX - panStart.current.x
    const dy = event.clientY - panStart.current.y
    setPan({
      x: panStart.current.panX + dx,
      y: panStart.current.panY + dy,
    })
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanning) return
    event.currentTarget.releasePointerCapture(event.pointerId)
    setIsPanning(false)
  }

  const handleDoubleClick = () => {
    if (zoom === 1) {
      applyZoom(2)
    } else {
      resetView()
    }
  }

  if (totalPages === 0 || !activeUrl) return null

  const zoomPercent = Math.round(zoom * 100)

  return (
    <GalleryWrapper aria-label={t('proofGallery.navigationAriaLabel')}>
      <ImageWrapper
        ref={wrapperRef}
        $isPanning={isPanning}
        $canPan={canPan}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
      >
        <Image
          data-testid="proof-gallery-image"
          src={activeUrl}
          alt={t('proofGallery.imageAltText', {
            pageNumber: displayedPageNumber,
          })}
          draggable={false}
          $zoom={zoom}
          $panX={pan.x}
          $panY={pan.y}
        />
      </ImageWrapper>

      <ControlsRow>
        {totalPages > 1 && (
          <Button
            type="button"
            padding="8px 16px"
            onClick={goPrev}
            disabled={activeIndex === 0}
            aria-label={t('proofGallery.previousButtonAriaLabel')}
          >
            {t('proofGallery.previousButtonLabel')}
          </Button>
        )}

        {totalPages > 1 && (
          <Text size="14px" weight="500" color="almostBlack">
            {t('proofGallery.pageIndicatorText', {
              currentPageNumber: displayedPageNumber,
              totalPageCount: totalPages,
            })}
          </Text>
        )}

        <ZoomGroup>
          <Button
            type="button"
            padding="6px 12px"
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            aria-label={t('proofGallery.zoomOutAriaLabel')}
          >
            −
          </Button>
          <Text size="14px" weight="500" color="almostBlack">
            {`${zoomPercent}%`}
          </Text>
          <Button
            type="button"
            padding="6px 12px"
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            aria-label={t('proofGallery.zoomInAriaLabel')}
          >
            +
          </Button>
        </ZoomGroup>

        {totalPages > 1 && (
          <Button
            type="button"
            padding="8px 16px"
            onClick={goNext}
            disabled={activeIndex === totalPages - 1}
            aria-label={t('proofGallery.nextButtonAriaLabel')}
          >
            {t('proofGallery.nextButtonLabel')}
          </Button>
        )}
      </ControlsRow>
    </GalleryWrapper>
  )
}

export default PdfProofGallery
